/* eslint-disable no-shadow */
import React, { useState } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";
import router from "next/router";
import toast from "react-hot-toast";

import DDQModalApprove from "./DDQModalApprove";
import DDQModalDecline from "./DDQModalDecline";
import DDQModalNotCompleted from "./DDQModalNotCompleted";
import { Props } from "./types";

import { Button } from "~/components/Button";
import { ButtonLink } from "~/components/Button";
import * as DetailList from "~/components/DetailList";
import * as Table from "~/components/Table";
import { performDocumentAccessQuery, useDocumentRequests, useTfSimpleVaspInfo } from "~/endpoints/trustFramework";
import {
  DocumentAccessAction,
  DocumentRequestInput,
  DocumentRequest,
  DocumentType,
  DocumentRequestStatus,
} from "~/endpoints/trustFramework/types";
import { BlurredDiv } from "~/templates/companies/styles";
import CellEntityAvatar from "~/templates/transactions/components/CellEntityAvatar";
import { CellEntityContainer, CellEntityTitle } from "~/templates/transactions/components/TransactionRow";
import SVGApprove from "~/templates/transactions/components/TransactionRow/components/SVGApprove";
import SVGCancel from "~/templates/transactions/components/TransactionRow/components/SVGCancel";
import SVGInfo from "~/templates/transactions/components/TransactionRow/components/SVGInfo";

function DDQManagementRow({
  docReq,
  hasCompletedForm,
  reloadDocumentRequests,
  allRequiredFieldsComplete,
}: {
  docReq: DocumentRequest;
  hasCompletedForm: boolean;
  reloadDocumentRequests: any;
  allRequiredFieldsComplete: boolean;
}) {
  const [isLoadingRow, setLoadingRow] = useState(false);
  const [showModalApprove, setShowModalApprove] = useState(false);
  const [showModalDecline, setShowModalDecline] = useState(false);
  const [showModalNotCompleted, setShowModalNotCompleted] = useState(false);
  const { ddqFlowRequiredFields: isEnabledDdqFlowRequiredFieldsFF } = useFlags();

  const { data: counterVaspSimpleInfoData } = useTfSimpleVaspInfo(
    docReq.requestingVASPdid,
    ["did", "name", "logo"],
    undefined,
    false,
    {
      dedupingInterval: 3600000, // Refetch once per hour...no reason to keep refetching these logos over and over
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  const logo = counterVaspSimpleInfoData?.logo || "/assets/vasp-fallback.png";

  async function actionHandler(action: DocumentAccessAction, metadata?: string | undefined): Promise<boolean> {
    try {
      const documentType = DocumentType.questionnaire;
      setLoadingRow(true);
      const ownerVASPDID = action === DocumentAccessAction.REQUEST ? docReq.requestingVASPdid : docReq.ownerVASPdid;
      const requestingVASPdid =
        action === DocumentAccessAction.REQUEST ? docReq.ownerVASPdid : docReq.requestingVASPdid;
      await performDocumentAccessQuery(ownerVASPDID, {
        requestingVASPdid,
        accessAction: action,
        documentType,

        ...(metadata && { metadata }),
      } as DocumentRequestInput);
      reloadDocumentRequests();
      return true;
    } catch (error) {
      toast.error((error as Error).toString());
      return false;
    } finally {
      setLoadingRow(false);
    }
  }
  async function handleClickApprove() {
    if (hasCompletedForm) setShowModalApprove(true);
    else setShowModalNotCompleted(true);
  }
  async function handleClickDecline() {
    if (hasCompletedForm) setShowModalDecline(true);
    else setShowModalNotCompleted(true);
  }
  async function handleClickDetails(vaspDID: string) {
    window.open("/dashboard/companies/" + vaspDID);
  }

  return (
    <>
      {showModalApprove && (
        <DDQModalApprove
          vaspName={counterVaspSimpleInfoData?.name || "Unknown VASP"}
          onClose={() => setShowModalApprove(false)}
          onSubmit={async (requestDDQAcess: boolean) => {
            let success = await actionHandler(DocumentAccessAction.APPROVE);
            if (success && requestDDQAcess) {
              success = await actionHandler(DocumentAccessAction.REQUEST);
              if (success) {
                toast.success("Access granted and request sent");
              }
            } else if (success) {
              toast.success("Access granted");
            }
            setShowModalApprove(false);
          }}
        />
      )}
      {showModalDecline && (
        <DDQModalDecline
          vaspName={counterVaspSimpleInfoData?.name || "Unknown VASP"}
          onClose={() => setShowModalDecline(false)}
          onSubmit={async (reason: string) => {
            if (await actionHandler(DocumentAccessAction.DECLINE, reason)) {
              toast.success("Access denied");
            }
            setShowModalDecline(false);
          }}
        />
      )}
      {showModalNotCompleted && (
        <DDQModalNotCompleted
          onClose={() => setShowModalNotCompleted(false)}
          onSubmit={() => {
            document.getElementsByTagName("main")[0].scrollTo({ top: 185, behavior: "smooth" });
            setShowModalNotCompleted(false);
          }}
        />
      )}
      <Table.TR>
        <Table.TD>
          <CellEntityContainer style={{ paddingRight: "0.2rem" }}>
            {logo && <CellEntityAvatar src={logo} />}
            <CellEntityTitle title={docReq.requestingVASPdid} className="localize-ignore">
              {counterVaspSimpleInfoData?.name || docReq.requestingVASPdid}
            </CellEntityTitle>
          </CellEntityContainer>
        </Table.TD>
        <Table.TD>{new Date(docReq.createdAt).toLocaleDateString("en-CA")}</Table.TD>
        <Table.TD style={{ textAlign: "right" }}>
          <ButtonLink
            title="Show VASP"
            data-testid="vaspDetailsButton"
            style={{ padding: "0 0.5rem" }}
            onClick={() => handleClickDetails(docReq.requestingVASPdid)}
            disabled={isLoadingRow}
          >
            <SVGInfo data-testid="info-icon" />
          </ButtonLink>
          <ButtonLink
            title="Approve access"
            style={{ padding: "0 0.5rem" }}
            color="success"
            onClick={handleClickApprove}
            disabled={isLoadingRow || (!allRequiredFieldsComplete && isEnabledDdqFlowRequiredFieldsFF)}
          >
            <SVGApprove style={{ fontSize: "1.5rem" }} />
          </ButtonLink>
          <ButtonLink
            title="Decline access"
            style={{ padding: "0 0.5rem" }}
            color="danger"
            onClick={handleClickDecline}
            disabled={isLoadingRow}
          >
            <SVGCancel style={{ fontSize: "1.5rem" }} />
          </ButtonLink>
        </Table.TD>
      </Table.TR>
    </>
  );
}

export default function DDQManagement({
  vasp,
  hasCompletedForm,
  userIsAuthenticated = false,
  allRequiredFieldsComplete = false,
}: Props) {
  const { data: _documentAccessRequests, mutate: reloadDocumentRequests } = useDocumentRequests(vasp?.did);
  // on the component we only want to show PENDING requests
  const documentAccessRequests = _documentAccessRequests?.filter(
    (doc: DocumentRequest) =>
      doc.documentType === DocumentType.questionnaire && doc.status === DocumentRequestStatus.PENDING,
  );

  if (!documentAccessRequests) return <></>;

  return (
    <BlurredDiv userIsAuthenticated={userIsAuthenticated}>
      <DetailList.Header>
        <DetailList.Item mobile={12} tablet={12} desktop={9}>
          <DetailList.Title as="h2">Requests to share your DDQ</DetailList.Title>
        </DetailList.Item>
      </DetailList.Header>
      <DetailList.Content>
        <Table.Container style={{ width: "100%" }}>
          <Table.Table narrow fullwidth={true}>
            <Table.THead>
              <Table.THR>
                <Table.TH>VASP</Table.TH>
                <Table.TH>Request date</Table.TH>
                <Table.TH style={{ textAlign: "right" }}>
                  <Button
                    style={{ textTransform: "none" }}
                    as="a"
                    kind="primary"
                    onClick={() => router.push("/dashboard/ddq-history")}
                  >
                    View Share History
                  </Button>
                </Table.TH>
              </Table.THR>
            </Table.THead>
            <Table.TBody style={{ border: "1px solid gray" }}>
              {!documentAccessRequests?.length && (
                <Table.TR>
                  <Table.TD colSpan={3} style={{ textAlign: "center" }}>
                    You have no requests
                  </Table.TD>
                </Table.TR>
              )}
              {documentAccessRequests?.map((docReq: DocumentRequest) => {
                const key: string = docReq.ownerVASPdid + docReq.requestingVASPdid + docReq.status;
                return (
                  <DDQManagementRow
                    docReq={docReq}
                    hasCompletedForm={!!hasCompletedForm}
                    reloadDocumentRequests={reloadDocumentRequests}
                    key={key}
                    allRequiredFieldsComplete={allRequiredFieldsComplete}
                  ></DDQManagementRow>
                );
              })}
            </Table.TBody>
          </Table.Table>
        </Table.Container>
      </DetailList.Content>
    </BlurredDiv>
  );
}
